/**
 * RFI Attachments API Client
 *
 * GW-3056: Client for gw-services-api RFI attachment endpoints.
 * Base URL: services.{env}.generalwisdom.com
 * All requests include Accept-Version: 1.0 header via ServicesApiClient.
 * GW-7632: uploadAttachment now returns SSD-backed multipart upload response.
 */

import type {
  PresignedDownloadResponse,
  PresignedUploadResponse,
  RFIAttachment,
  RFIAttachmentListResponse,
} from '@/types/rfi-attachment.types';

import { ServicesApiClient } from './servicesApiClient';

class RFIAttachmentsAPI extends ServicesApiClient {
  /**
   * Initiate a multipart upload for an attachment.
   * POST /rfis/:rfiId/attachments
   * GW-7632: Returns SSD-backed multipart upload fields:
   *   { attachmentId, uploadId, parts, completeUrl, s3Key }
   * Flow: POST here → PUT each part presignedUrl → POST completeUrl
   */
  async uploadAttachment(
    rfiId: string,
    data: { fileName: string; fileSize: number; contentType: string }
  ): Promise<PresignedUploadResponse> {
    return this.post<PresignedUploadResponse>(`/rfis/${rfiId}/attachments`, data);
  }

  /**
   * List attachments for an RFI.
   * GET /rfis/:rfiId/attachments
   */
  async listAttachments(
    rfiId: string,
    params?: { limit?: number; cursor?: string }
  ): Promise<RFIAttachmentListResponse> {
    const qs = params ? this.buildQueryString(params) : '';
    return this.get<RFIAttachmentListResponse>(`/rfis/${rfiId}/attachments${qs}`);
  }

  /**
   * Delete an attachment.
   * DELETE /rfis/:rfiId/attachments/:attachmentId
   */
  async deleteAttachment(rfiId: string, attachmentId: string): Promise<void> {
    return this.delete<void>(`/rfis/${rfiId}/attachments/${attachmentId}`);
  }

  /**
   * Flag an attachment for review.
   * POST /rfis/:rfiId/attachments/:attachmentId/flag
   */
  async flagAttachment(rfiId: string, attachmentId: string): Promise<RFIAttachment> {
    return this.post<RFIAttachment>(`/rfis/${rfiId}/attachments/${attachmentId}/flag`);
  }

  /**
   * Get a presigned download URL for an attachment.
   * POST /rfis/:rfiId/attachments/:attachmentId/download
   */
  async downloadAttachment(
    rfiId: string,
    attachmentId: string
  ): Promise<PresignedDownloadResponse> {
    return this.post<PresignedDownloadResponse>(
      `/rfis/${rfiId}/attachments/${attachmentId}/download`
    );
  }

  /**
   * Confirm an attachment, marking it as accepted by the buyer.
   * Triggers the consultant payout flow on the backend.
   * POST /rfis/:rfiId/attachments/:attachmentId/confirm
   */
  async confirmAttachment(rfiId: string, attachmentId: string): Promise<RFIAttachment> {
    return this.post<RFIAttachment>(`/rfis/${rfiId}/attachments/${attachmentId}/confirm`);
  }
}

export const rfiAttachmentsApi = new RFIAttachmentsAPI();
